import React from "react";
import PropTypes from "prop-types";
import {makeStyles, Grid, Box, Typography} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import InformationCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/alerts/information-circle.svg";
import {useI18N} from "../../../common/common";
import {retrieveContentfulImage} from "../../../common/image";
import AccessibleIcon from "../../widget/streamline-icon/AccessibleIcon";
import CustomPopover from "../../widget/popover/CustomPopover";

const useStyles = makeStyles((theme) => ({
    apartmentImage: {
        objectFit: "cover",
        width: "100%",
        maxWidth: "172px",
        maxHeight: "124px",
        [theme.breakpoints.up("sm")]: {
            maxWidth: "221px"
        }
    },
    apartmentInfo: {
        display: "inline-flex",
        alignItems: "center"
    },
    iconBox: {
        borderRadius: theme.spacing(0.5),
        border: `1px solid ${theme.palette.text.white}`,
        background: theme.palette.text.link,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: theme.spacing(3.5),
        height: theme.spacing(3.5),
        "& i": {
            color: theme.palette.text.white,
            fontSize: theme.spacing(2)
        }
    },
    hyphenate: {
        wordBreak: "normal",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        "-webkit-hyphens": "auto",
        "-moz-hyphens": "auto",
        hyphens: "auto"
    },
    w100: {
        width: "100%"
    },
    infoIcon: {
        "& > i": {
            width: theme.spacing(2),
            height: theme.spacing(2),
            marginTop: theme.spacing(0.5),
            marginRight: theme.spacing(0) + " !important",
            marginLeft: theme.spacing(1),
            color: theme.palette.grey[400]
        },
    },
    maxWidth320: {
        maxWidth: 320
    },
}));

const BookingApartmentSummary = ({apartment, resortName, loading}) => {

    const classes = useStyles();
    const {i18n} = useI18N();

    return (
        <Grid
            container
            direction={"row"}
            wrap={"nowrap"}
        >
            {loading ? (
                <Skeleton
                    variant={"rect"}
                    height={124}
                    classes={{root: classes.apartmentImage}}
                />
            ) : (
                <img
                    alt={apartment?.apartment_name}
                    src={retrieveContentfulImage(apartment?.apartment_picture, {width: 221, height: 124, fit: "thumb"})}
                    className={classes.apartmentImage}
                />
            )}

            <Grid
                item
                xs={12}
            >
                <Box
                    mx={2}
                    my={1.5}
                >
                    <Typography
                        variant="h2"
                        className={classes.hyphenate}
                    >
                        {loading ? <Skeleton /> : resortName}
                    </Typography>

                    <Grid
                        container
                        direction={"row"}
                        wrap={"nowrap"}
                    >
                        <Grid
                            item
                            xs={12}
                            className={classes.apartmentInfo}
                        >
                            <Typography
                                variant="body2"
                                className={classes.w100}
                            >
                                {loading ? <Skeleton /> : apartment?.apartment_size_name + " " + apartment?.apartment_name}
                            </Typography>

                            {apartment?.wheelchair_accessibility &&
                                <Box
                                    className={classes.iconBox}
                                    ml={1}
                                >
                                    <AccessibleIcon />
                                </Box>
                            }
                        </Grid>
                    </Grid>
                    {loading ? (
                        <Typography
                            variant="body2"
                        >
                            <Skeleton />
                        </Typography>
                    ) : (
                        <Box
                            display="flex"
                            alignItems="center"
                        >
                            <Typography
                                variant="body2"
                            >
                                <Box
                                    component="span"
                                    display="inline-block"
                                    mr={0.5}
                                >
                                    {apartment?.extra_bed ?
                                        (i18n("view.apartment_card.max_number_of_guests", {numOfGuests: apartment.max_number_of_guests - 1, plusExtraBed: "yes"})) :
                                        (i18n("view.apartment_card.max_number_of_guests", {numOfGuests: apartment.max_number_of_guests, plusExtraBed: "no"}))
                                    }
                                </Box>

                                <Box
                                    component="span"
                                    display="inline-block"
                                >
                                    {apartment?.max_number_of_adults && (`(${i18n("view.apartment_card.max_number_of_adults", {numOfAdults: apartment?.max_number_of_adults})})`)}
                                </Box>
                            </Typography>

                            {apartment?.guest_info && (
                                <CustomPopover
                                    title={apartment.guest_info?.title}
                                    icon={<InformationCircle />}
                                    iconClassName={classes.infoIcon}
                                >
                                    <Box
                                        pb={1}
                                        px={1.5}
                                        className={classes.maxWidth320}
                                    >
                                        <Typography
                                            variant="body2"
                                        >
                                            {apartment.guest_info?.description}
                                        </Typography>
                                    </Box>
                                </CustomPopover>
                            )}
                        </Box>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

BookingApartmentSummary.propTypes = {
    apartment: PropTypes.object,
    resortName: PropTypes.string,
    loading: PropTypes.bool
};

export default BookingApartmentSummary;
